import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import {
  Plus, Loader2, AlertTriangle, Shield, Clock, Download,
  FileText, CheckCircle, XCircle, Bell, ChevronDown, ChevronRight, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { getBreaches, createBreach, updateBreach, deleteBreach, downloadDPBLetter, downloadPrincipalLetter } from '@/services/dpdpa';

const SEVERITIES = ['critical', 'high', 'medium', 'low'];
const CATEGORIES = [
  { value: 'unauthorized_access', label: 'Unauthorized Access' },
  { value: 'data_leak', label: 'Data Leak / Exposure' },
  { value: 'system_compromise', label: 'System Compromise' },
  { value: 'insider_threat', label: 'Insider Threat' },
  { value: 'ransomware', label: 'Ransomware Attack' },
  { value: 'phishing', label: 'Phishing / Social Engineering' },
  { value: 'lost_device', label: 'Lost / Stolen Device' },
  { value: 'third_party', label: 'Third-Party Breach' },
  { value: 'other', label: 'Other' },
];
const DATA_CATS = ['personal_identifiers', 'contact_information', 'financial_data', 'health_data', 'biometric_data', 'behavioral_data', 'location_data'];
const STATUS_FLOW = ['detected', 'investigating', 'contained', 'dpb_notified', 'principals_notified', 'resolved', 'closed'];

const severityColor = (s) => {
  const m = { critical: 'bg-red-600 text-white', high: 'bg-orange-500 text-white', medium: 'bg-amber-500 text-white', low: 'bg-blue-500 text-white' };
  return m[s] || 'bg-gray-500 text-white';
};

const countdownColor = (status) => {
  const m = { overdue: 'text-red-600', critical: 'text-red-500', urgent: 'text-orange-500', warning: 'text-amber-500', on_track: 'text-emerald-500', notified: 'text-blue-500' };
  return m[status] || 'text-gray-500';
};

const countdownBg = (status) => {
  const m = { overdue: 'bg-red-500/10 border-red-500/30', critical: 'bg-red-500/10 border-red-500/20', urgent: 'bg-orange-500/10 border-orange-500/20', warning: 'bg-amber-500/10 border-amber-500/20', on_track: 'bg-emerald-500/10 border-emerald-500/20', notified: 'bg-blue-500/10 border-blue-500/20' };
  return m[status] || 'bg-gray-500/10 border-gray-500/20';
};

const CountdownTimer = ({ countdown }) => {
  if (!countdown) return null;
  const hrs = countdown.hours_remaining;
  const pct = countdown.status === 'notified' ? 100 : Math.max(0, Math.min(100, (hrs / 72) * 100));
  return (
    <div className={`p-3 rounded-lg border ${countdownBg(countdown.status)}`} data-testid="countdown-timer">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium flex items-center gap-1">
          <Clock className="w-3 h-3" /> 72h DPB Notification Deadline
        </span>
        <span className={`text-sm font-bold ${countdownColor(countdown.status)}`}>
          {countdown.status === 'notified' ? 'DPB Notified' : countdown.expired ? 'OVERDUE' : `${hrs.toFixed(1)}h remaining`}
        </span>
      </div>
      <Progress value={pct} className="h-2" />
      <div className="flex justify-between text-xs opacity-50 mt-1">
        <span>0h</span><span>24h</span><span>48h</span><span>72h</span>
      </div>
    </div>
  );
};

export default function BreachTrackerPage() {
  const [breaches, setBreaches] = useState([]);
  const [categories, setCategories] = useState(CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showStatusUpdate, setShowStatusUpdate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [form, setForm] = useState({
    title: '', severity: 'high', category: 'unauthorized_access', description: '',
    data_categories_affected: [], affected_count: 'Under investigation', impact: '',
    remediation_steps: [''], systems_affected: [''], discovered_at: new Date().toISOString().slice(0, 16),
    org_name: '', org_address: '', dpo_name: '', dpo_email: '',
  });
  const [statusForm, setStatusForm] = useState({ status: '', resolution_notes: '' });

  const fetchBreaches = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getBreaches();
      setBreaches(res.breaches || []);
      if (res.categories) setCategories(res.categories);
    } catch (err) {
      toast.error('Failed to load breaches');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBreaches(); }, [fetchBreaches]);

  const handleCreate = async () => {
    if (!form.title) { toast.error('Title is required'); return; }
    try {
      setSaving(true);
      const payload = {
        ...form,
        remediation_steps: form.remediation_steps.filter(s => s.trim()),
        systems_affected: form.systems_affected.filter(s => s.trim()),
      };
      await createBreach(payload);
      toast.success('Breach registered — 72h countdown started');
      setShowCreate(false);
      fetchBreaches();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create breach');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!statusForm.status) { toast.error('Select a status'); return; }
    try {
      setSaving(true);
      const update = { status: statusForm.status };
      if (statusForm.resolution_notes) update.resolution_notes = statusForm.resolution_notes;
      if (statusForm.status === 'dpb_notified') update.dpb_notified = true;
      if (statusForm.status === 'principals_notified') update.principals_notified = true;
      await updateBreach(showStatusUpdate.id, update);
      toast.success('Status updated');
      setShowStatusUpdate(null);
      setStatusForm({ status: '', resolution_notes: '' });
      fetchBreaches();
    } catch (err) {
      toast.error('Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadDPB = async (breach) => {
    try {
      toast.info('Generating DPB notification letter...');
      const blob = await downloadDPBLetter(breach.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DPB_Notification_${breach.id.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('DPB letter downloaded');
    } catch (err) {
      toast.error('Failed to generate letter');
    }
  };

  const handleDownloadPrincipal = async (breach) => {
    try {
      toast.info('Generating principal notification...');
      const blob = await downloadPrincipalLetter(breach.id, 'affected@example.com');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Principal_Notification_${breach.id.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Principal notification downloaded');
    } catch (err) {
      toast.error('Failed to generate letter');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this breach record?')) return;
    try { await deleteBreach(id); toast.success('Deleted'); fetchBreaches(); } catch { toast.error('Failed'); }
  };

  const toggleDataCat = (cat) => {
    setForm(prev => ({
      ...prev,
      data_categories_affected: prev.data_categories_affected.includes(cat) ? prev.data_categories_affected.filter(c => c !== cat) : [...prev.data_categories_affected, cat]
    }));
  };

  const updateListField = (field, idx, value) => {
    setForm(prev => {
      const arr = [...prev[field]];
      arr[idx] = value;
      return { ...prev, [field]: arr };
    });
  };

  const addListItem = (field) => setForm(prev => ({ ...prev, [field]: [...prev[field], ''] }));

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin opacity-50" /></div>;

  // Stats
  const active = breaches.filter(b => !['resolved', 'closed'].includes(b.status));
  const overdue = breaches.filter(b => b.countdown?.status === 'overdue');
  const critical = breaches.filter(b => b.severity === 'critical' && !['resolved', 'closed'].includes(b.status));

  return (
    <div className="p-6 space-y-6" data-testid="breach-tracker-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Breach Notification Tracker</h1>
          <p className="text-sm opacity-60">DPDPA Section 8(6) — 72-hour notification to DPB and affected Data Principals</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="report-breach-btn">
          <Plus className="w-4 h-4 mr-2" /> Report Breach
        </Button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{breaches.length}</p><p className="text-xs opacity-60">Total Breaches</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-orange-500">{active.length}</p><p className="text-xs opacity-60">Active</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-red-600">{critical.length}</p><p className="text-xs opacity-60">Critical Active</p></CardContent></Card>
        <Card className={overdue.length > 0 ? 'border-red-500/50 border-2' : ''}><CardContent className="p-4 text-center"><p className={`text-2xl font-bold ${overdue.length > 0 ? 'text-red-600' : ''}`}>{overdue.length}</p><p className="text-xs opacity-60">Overdue (72h)</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-blue-500">{breaches.filter(b => b.dpb_notified).length}</p><p className="text-xs opacity-60">DPB Notified</p></CardContent></Card>
      </div>

      {/* Breach List */}
      {breaches.length === 0 ? (
        <Card><CardContent className="p-8 text-center opacity-50"><Shield className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>No data breaches recorded. Use "Report Breach" to log an incident.</p></CardContent></Card>
      ) : (
        <div className="space-y-3">
          {breaches.map((b) => (
            <Card key={b.id} data-testid={`breach-card-${b.id}`} className={b.countdown?.status === 'overdue' ? 'border-l-4 border-l-red-500' : b.countdown?.status === 'critical' ? 'border-l-4 border-l-orange-500' : ''}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h3 className="font-semibold">{b.title}</h3>
                      <Badge className={severityColor(b.severity)}>{b.severity}</Badge>
                      <Badge variant="outline" className="text-xs capitalize">{b.status.replace(/_/g, ' ')}</Badge>
                      <Badge variant="outline" className="text-xs">{(categories.find(c => c.value === b.category) || {}).label || b.category}</Badge>
                      {b.dpb_notified && <Badge className="bg-blue-500/10 text-blue-700 text-xs"><CheckCircle className="w-3 h-3 mr-1" />DPB Notified</Badge>}
                      {b.principals_notified && <Badge className="bg-emerald-500/10 text-emerald-700 text-xs"><CheckCircle className="w-3 h-3 mr-1" />Principals Notified</Badge>}
                    </div>
                    {b.description && <p className="text-sm opacity-60 mb-2">{b.description.slice(0, 150)}{b.description.length > 150 ? '...' : ''}</p>}
                    <CountdownTimer countdown={b.countdown} />
                    <div className="flex gap-4 mt-2 text-xs opacity-50">
                      <span>Discovered: {new Date(b.discovered_at).toLocaleDateString()}</span>
                      <span>Affected: {b.affected_count}</span>
                      {b.data_categories_affected?.length > 0 && <span>Data: {b.data_categories_affected.map(c => c.replace(/_/g, ' ')).join(', ')}</span>}
                    </div>
                    {/* Timeline toggle */}
                    {b.timeline?.length > 0 && (
                      <button onClick={() => setExpanded(p => ({ ...p, [b.id]: !p[b.id] }))} className="text-xs text-blue-500 mt-2 flex items-center gap-1">
                        {expanded[b.id] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />} Timeline ({b.timeline.length})
                      </button>
                    )}
                    {expanded[b.id] && b.timeline && (
                      <div className="mt-2 ml-2 border-l-2 border-blue-200 pl-3 space-y-1">
                        {b.timeline.map((t, i) => (
                          <div key={i} className="text-xs"><span className="opacity-40">{new Date(t.timestamp).toLocaleString()}</span> — <span className="font-medium">{t.action?.replace(/_/g, ' ')}</span> <span className="opacity-50">by {t.actor}</span>{t.notes && <span className="opacity-50"> — {t.notes}</span>}</div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    {b.status !== 'resolved' && b.status !== 'closed' && (
                      <Button variant="outline" size="sm" onClick={() => { setShowStatusUpdate(b); setStatusForm({ status: '', resolution_notes: '' }); }} data-testid={`update-breach-${b.id}`}>Update Status</Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => handleDownloadDPB(b)} data-testid={`dpb-letter-${b.id}`}><Download className="w-3 h-3 mr-1" />DPB Letter</Button>
                    <Button variant="outline" size="sm" onClick={() => handleDownloadPrincipal(b)} data-testid={`principal-letter-${b.id}`}><FileText className="w-3 h-3 mr-1" />Principal Notice</Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(b.id)}><Trash2 className="w-4 h-4 text-red-400" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Breach Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-breach-dialog">
          <DialogHeader><DialogTitle>Report Data Breach</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title</Label><Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Customer database unauthorized access" data-testid="breach-title-input" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Severity</Label>
                <Select value={form.severity} onValueChange={v => setForm(p => ({ ...p, severity: v }))}>
                  <SelectTrigger data-testid="breach-severity-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{SEVERITIES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Discovered At</Label><Input type="datetime-local" value={form.discovered_at} onChange={e => setForm(p => ({ ...p, discovered_at: e.target.value }))} data-testid="breach-discovered-input" /></div>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={3} placeholder="Describe the breach incident..." data-testid="breach-desc-input" /></div>
            <div><Label>Data Categories Affected</Label>
              <div className="flex flex-wrap gap-2 mt-1">{DATA_CATS.map(c => (
                <Badge key={c} variant={form.data_categories_affected.includes(c) ? 'default' : 'outline'} className="cursor-pointer text-xs capitalize" onClick={() => toggleDataCat(c)}>{c.replace(/_/g, ' ')}</Badge>
              ))}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Affected Count</Label><Input value={form.affected_count} onChange={e => setForm(p => ({ ...p, affected_count: e.target.value }))} placeholder="e.g. 5000 or Under investigation" /></div>
              <div><Label>Impact</Label><Input value={form.impact} onChange={e => setForm(p => ({ ...p, impact: e.target.value }))} placeholder="Potential consequences..." /></div>
            </div>
            <div>
              <Label>Remediation Steps</Label>
              {form.remediation_steps.map((s, i) => (
                <Input key={i} value={s} onChange={e => updateListField('remediation_steps', i, e.target.value)} placeholder={`Step ${i + 1}`} className="mt-1" />
              ))}
              <Button variant="ghost" size="sm" onClick={() => addListItem('remediation_steps')} className="mt-1">+ Add Step</Button>
            </div>
            <div className="border-t pt-3">
              <Label className="text-sm font-semibold">Organization Info (for DPB Letter)</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div><Label>Organization Name</Label><Input value={form.org_name} onChange={e => setForm(p => ({ ...p, org_name: e.target.value }))} placeholder="Company Name" data-testid="breach-org-input" /></div>
                <div><Label>DPO Name</Label><Input value={form.dpo_name} onChange={e => setForm(p => ({ ...p, dpo_name: e.target.value }))} placeholder="Data Protection Officer" /></div>
                <div><Label>DPO Email</Label><Input value={form.dpo_email} onChange={e => setForm(p => ({ ...p, dpo_email: e.target.value }))} placeholder="dpo@company.com" /></div>
                <div><Label>Organization Address</Label><Input value={form.org_address} onChange={e => setForm(p => ({ ...p, org_address: e.target.value }))} placeholder="Registered Address" /></div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="submit-breach-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />} Report Breach
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={!!showStatusUpdate} onOpenChange={v => { if (!v) setShowStatusUpdate(null); }}>
        <DialogContent data-testid="update-breach-status-dialog">
          <DialogHeader><DialogTitle>Update Breach Status</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>New Status</Label>
              <Select value={statusForm.status} onValueChange={v => setStatusForm(p => ({ ...p, status: v }))}>
                <SelectTrigger data-testid="breach-new-status"><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>{STATUS_FLOW.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label><Textarea value={statusForm.resolution_notes} onChange={e => setStatusForm(p => ({ ...p, resolution_notes: e.target.value }))} placeholder="Resolution notes..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusUpdate(null)}>Cancel</Button>
            <Button onClick={handleStatusUpdate} disabled={saving} data-testid="confirm-breach-status-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
